import * as React from "react";
import {
	alpha,
	Box,
	ButtonBase,
	Dialog,
	DialogContent,
	Fade,
	IconButton,
	Skeleton,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import {makeStyles} from "tss-react/mui";
import {useMq} from "@buildwitt/shapeshift-common/v1/hooks";
import {useSSTranslation} from "@/i18n/useSSTranslation";
import type {II18nMap} from "@/i18n/en";
import {useListAwardEvents, type AwardEventResponseDto} from "@/app2/shared/data";

const useStyles = makeStyles<{isMobile: boolean}>()((theme, {isMobile}) => {
	const dialogPadding = isMobile ? theme.spacing(2) : theme.spacing(4);
	const subtitleTypo = theme.typography.subtitle1;
	const overlineTypo = theme.typography.overline ?? theme.typography.caption;
	const h4Typo = theme.typography.h4;
	const titleStripPad = theme.spacing(2);

	return {
		dialogBackdrop: {
			backgroundColor: alpha(theme.palette.common.black, theme.palette.action.disabledOpacity)
		},
		dialogPaper: {
			backgroundColor: theme.palette.background.paperElevation2,
			borderRadius: theme.spacing(2),
			color: theme.palette.text.primary,
			marginTop: isMobile ? 0 : theme.spacing(7.625),
			maxWidth: isMobile ? "100%" : theme.spacing(90),
			width: "100%",
			boxSizing: "border-box",
			position: "relative",
			overflow: "hidden",
			border: `${theme.spacing(0.125)} solid ${theme.palette.divider}`
		},
		closeButtonInStrip: {
			flexShrink: 0,
			alignSelf: "center",
			color: theme.palette.text.primary
		},
		contentRoot: {
			paddingLeft: dialogPadding,
			paddingRight: dialogPadding,
			paddingBottom: dialogPadding,
			paddingTop: 0,
			minWidth: 0,
			overflowX: "hidden",
			"&:first-of-type": {
				paddingTop: 0
			}
		},
		companyTitle: {
			fontFamily: theme.typography.fontFamily,
			fontSize: h4Typo.fontSize,
			lineHeight: h4Typo.lineHeight,
			fontWeight: theme.typography.fontWeightBold,
			color: theme.palette.text.primary,
			textAlign: "left",
			width: "100%",
			margin: 0
		},
		titleCloseStripGrow: {
			flex: "1 1 auto",
			minWidth: 0,
			display: "flex",
			alignItems: "center",
			alignSelf: "stretch"
		},
		titleCloseStrip: {
			display: "flex",
			flexDirection: "row",
			alignItems: "center",
			justifyContent: "flex-start",
			gap: theme.spacing(1),
			width: "100%",
			boxSizing: "border-box",
			minHeight: 0,
			paddingTop: titleStripPad,
			paddingBottom: titleStripPad
		},
		topBar: {
			display: "flex",
			flexDirection: "row",
			alignItems: "center",
			justifyContent: "flex-start",
			marginBottom: theme.spacing(3),
			minHeight: theme.spacing(5)
		},
		backButton: {
			display: "inline-flex",
			flexDirection: "row",
			alignItems: "center",
			gap: theme.spacing(0.5),
			color: theme.palette.primary.main,
			fontFamily: theme.typography.fontFamily,
			fontSize: subtitleTypo.fontSize,
			lineHeight: subtitleTypo.lineHeight,
			fontWeight: theme.typography.fontWeightBold,
			textTransform: "uppercase",
			letterSpacing: overlineTypo.letterSpacing ?? "0.06em",
			padding: theme.spacing(0.5, 1),
			marginLeft: theme.spacing(-1),
			borderRadius: theme.shape.borderRadius,
			textAlign: "left"
		},
		backIcon: {
			fontSize: theme.spacing(2.5),
			display: "flex"
		},
		backButtonLabel: {
			fontWeight: "inherit",
			color: "inherit",
			fontSize: "inherit",
			fontFamily: "inherit",
			lineHeight: "inherit",
			textTransform: "inherit",
			letterSpacing: "inherit"
		},
		headerContentDivider: {
			width: "100%",
			height: 0,
			margin: 0,
			marginTop: 0,
			marginBottom: theme.spacing(2),
			border: "none",
			borderTop: `${theme.spacing(0.125)} solid ${theme.palette.divider}`,
			flexShrink: 0
		},
		tableOuter: {
			border: `${theme.spacing(0.125)} solid ${theme.palette.divider}`,
			borderRadius: theme.spacing(1),
			overflow: "hidden",
			width: "100%",
			maxWidth: "100%"
		},
		table: {
			borderCollapse: "collapse",
			width: "100%",
			tableLayout: "fixed"
		},
		colEvent: {
			width: "45%"
		},
		colPoints: {
			width: "25%"
		},
		colFrequency: {
			width: "30%"
		},
		tableHeadCell: {
			fontFamily: theme.typography.fontFamily,
			fontSize: subtitleTypo.fontSize,
			lineHeight: subtitleTypo.lineHeight,
			fontWeight: theme.typography.fontWeightBold,
			color: theme.palette.text.primary,
			borderBottom: `${theme.spacing(0.125)} solid ${theme.palette.divider}`,
			padding: theme.spacing(2, 2),
			verticalAlign: "top",
			wordBreak: "break-word",
			overflowWrap: "anywhere",
			maxWidth: 0
		},
		tableBodyCell: {
			fontFamily: theme.typography.fontFamily,
			fontSize: subtitleTypo.fontSize,
			lineHeight: subtitleTypo.lineHeight,
			fontWeight: theme.typography.fontWeightRegular ?? 400,
			color: theme.palette.text.primary,
			borderBottom: `${theme.spacing(0.125)} solid ${theme.palette.divider}`,
			padding: theme.spacing(2, 2),
			verticalAlign: "top",
			wordBreak: "break-word",
			overflowWrap: "anywhere",
			maxWidth: 0
		},
		tableBodyCellLast: {
			borderBottom: "none"
		}
	};
});

export type LeaderboardPointsGuideModalProps = {
	open: boolean;
	onClose: () => void;
};

const emptyCell = "\u2014";

function trimUnknownToString(value: unknown): string {
	if (value == null || value === false) return "";
	if (typeof value === "string") return value.trim();
	return String(value).trim();
}

function displayOrDash(value: string): string {
	const s = value.trim();
	return s ? s : emptyCell;
}

function awardEventRawName(row: AwardEventResponseDto): string {
	const fromName = trimUnknownToString(row.name);
	if (fromName) return fromName;
	return trimUnknownToString(row.description);
}

const AWARD_EVENT_I18N: Record<number, {key: keyof II18nMap; defaultValue: string}> = {
	1: {key: "leaderboard_points_event_completed_lesson", defaultValue: "Completed Lesson"},
	2: {key: "leaderboard_points_event_completed_course", defaultValue: "Completed Course"},
	3: {key: "leaderboard_points_event_quiz_perfect", defaultValue: "Quiz Complete 100% Correct"}
};

export const LeaderboardPointsGuideModal: React.FC<LeaderboardPointsGuideModalProps> = ({
	open,
	onClose
}) => {
	const mq = useMq();
	const {classes, cx} = useStyles({isMobile: mq.mobile});
	const {t} = useSSTranslation();

	const {
		data: awardList,
		isPending: isAwardListPending,
		isFetching: isAwardListFetching,
		isError: isAwardListError
	} = useListAwardEvents({enabled: open});

	const isLoading =
		open &&
		awardList === undefined &&
		!isAwardListError &&
		(isAwardListPending || isAwardListFetching);

	const items = awardList?.items ?? [];

	/** Frequency column is fixed copy (not returned by award-events). */
	const frequencyLabelHardcoded = t(
		"leaderboard_points_frequency_recurring",
		"Recurring"
	);

	const backLabel = t("leaderboard_points_guide_back", "Back to leaderboard");

	const handleDialogClose = (_event: unknown, reason: "backdropClick" | "escapeKeyDown") => {
		if (reason === "backdropClick" || reason === "escapeKeyDown") {
			onClose();
		}
	};

	const eventCol = t("leaderboard_points_guide_col_event", "Event");
	const pointsCol = t("leaderboard_points_guide_col_points", "Points");
	const frequencyCol = t("leaderboard_points_guide_col_frequency", "Frequency");

	return (
		<Dialog
			open={open}
			fullScreen={mq.mobile}
			onClose={handleDialogClose}
			TransitionComponent={Fade}
			aria-labelledby="leaderboard-points-guide-company-title"
			aria-modal="true"
			data-test-id="leaderboard-points-guide-modal"
			BackdropProps={{
				className: classes.dialogBackdrop
			}}
			PaperProps={{
				className: classes.dialogPaper
			}}
		>
			<DialogContent className={classes.contentRoot} data-test-id="leaderboard-points-guide-modal-content">
				<Box className={classes.titleCloseStrip}>
					<Box className={classes.titleCloseStripGrow}>
						<Typography
							id="leaderboard-points-guide-company-title"
							className={classes.companyTitle}
							component="h1"
							variant="h4"
							data-test-id="leaderboard-points-guide-company-title"
						>
							{t("leaderboard_modal_company_title", "Company Leaderboard")}
						</Typography>
					</Box>
					<IconButton
						onClick={onClose}
						className={classes.closeButtonInStrip}
						aria-label={t("leaderboard_points_guide_close", "Close points guide")}
						data-test-id="leaderboard-points-guide-modal-close-btn"
						edge="end"
						size="medium"
					>
						<CloseIcon />
					</IconButton>
				</Box>

				<Box className={classes.headerContentDivider} component="hr" aria-hidden />

				<Box className={classes.topBar}>
					<ButtonBase
						className={classes.backButton}
						onClick={onClose}
						aria-label={backLabel}
						data-test-id="leaderboard-points-guide-modal-back-btn"
					>
						<ChevronLeftIcon className={classes.backIcon} aria-hidden />
						<Box component="span" className={classes.backButtonLabel}>
							{backLabel}
						</Box>
					</ButtonBase>
				</Box>

				<TableContainer
					className={classes.tableOuter}
					component={Box}
					sx={{overflowX: "hidden", maxWidth: "100%"}}
				>
					<Table className={classes.table} aria-label={t("leaderboard_points_guide_table_aria", "Points breakdown")}>
						<colgroup>
							<col className={classes.colEvent} />
							<col className={classes.colPoints} />
							<col className={classes.colFrequency} />
						</colgroup>
						<TableHead>
							<TableRow>
								<TableCell className={classes.tableHeadCell}>{eventCol}</TableCell>
								<TableCell className={classes.tableHeadCell}>{pointsCol}</TableCell>
								<TableCell className={classes.tableHeadCell}>{frequencyCol}</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{isAwardListError ? (
								<TableRow>
									<TableCell className={classes.tableBodyCell} colSpan={3}>
										<Typography variant="body2" color="text.secondary">
											{t(
												"leaderboard_points_guide_load_error",
												"Could not load point events. Try again later."
											)}
										</Typography>
									</TableCell>
								</TableRow>
							) : isLoading ? (
								Array.from({length: 5}).map((_, index) => {
									const isLast = index === 4;
									const cellClass = cx(
										classes.tableBodyCell,
										isLast && classes.tableBodyCellLast
									);
									return (
										<TableRow key={index}>
											<TableCell className={cellClass}>
												<Skeleton variant="text" width="80%" height={24} />
											</TableCell>
											<TableCell className={cellClass}>
												<Skeleton variant="text" width={48} height={24} />
											</TableCell>
											<TableCell className={cellClass}>
												<Skeleton variant="text" width="60%" height={24} />
											</TableCell>
										</TableRow>
									);
								})
							) : items.length === 0 ? (
								<TableRow>
									<TableCell className={cx(classes.tableBodyCell, classes.tableBodyCellLast)} colSpan={3}>
										<Typography variant="body2" color="text.secondary">
											{t(
												"leaderboard_points_guide_list_empty",
												"No point events are configured yet."
											)}
										</Typography>
									</TableCell>
								</TableRow>
							) : (
								items.map((row, index) => {
									const isLast = index === items.length - 1;
									const cellClass = cx(classes.tableBodyCell, isLast && classes.tableBodyCellLast);
									const i18nEntry = AWARD_EVENT_I18N[row.id];
									const eventLabel = i18nEntry
										? t(i18nEntry.key, i18nEntry.defaultValue)
										: displayOrDash(awardEventRawName(row));
									return (
										<TableRow key={row.id}>
											<TableCell className={cellClass}>{eventLabel}</TableCell>
											<TableCell className={cellClass}>
												{t("leaderboard_points_px", "{{count}} PX", {count: row.points})}
											</TableCell>
											<TableCell className={cellClass}>{frequencyLabelHardcoded}</TableCell>
										</TableRow>
									);
								})
							)}
						</TableBody>
					</Table>
				</TableContainer>
			</DialogContent>
		</Dialog>
	);
};
